import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { combineEpicsIsolated } from 'utils/epicRegistry'

import { WIZARD_CREATE_TENANT } from 'containers/OnboardingWizard/constants'

import { addToast } from '../Toasts/actions'

import { EDIT_TENANT_APPLY_ASYNC } from './EditTenant/constants'
import editTenantEpics from './EditTenant/epics'
import {
  addCompanyError,
  addCompanySuccess,
  fetchCompaniesError,
  fetchCompaniesSuccess,
  fetchPartnerIncidentsError,
  fetchPartnerIncidentsSuccess,
  fetchPartnerStatisticsError,
  fetchPartnerStatisticsSuccess,
  submitPartnerSuccessSubmissionError,
  submitPartnerSuccessSubmissionSuccess,
} from './actions'
import {
  COMPANIES_FILTER_CHANGE,
  COMPANIES_PAGE_CHANGE,
  FETCH_POSSIBLE_ACQUIRING_PARTNERS_ASYNC,
  INCIDENTS_FILTER_CHANGE,
  MIGRATE_COMPANY_ASYNC,
  NEW_COMPANY_ADD_ASYNC,
  PARTNER_COMPANIES_GET_ASYNC,
  PARTNER_INCIDENTS_GET_ASYNC,
  PARTNER_STATISTICS_GET_ASYNC,
  PARTNER_SUCCESS_SUBMISSION_SEND_ASYNC,
  SEARCH_CHANGE,
  SORT_PROPERTY_CHANGE,
} from './constants'
import {
  selectCompaniesFilter,
  selectPage,
  selectSearch,
  selectSortProperty,
  selectTimeFilter,
} from './selectors'

const fetchCompaniesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      NEW_COMPANY_ADD_ASYNC.SUCCESS,
      EDIT_TENANT_APPLY_ASYNC.SUCCESS,
      PARTNER_COMPANIES_GET_ASYNC.INITIATE,
      COMPANIES_PAGE_CHANGE,
      COMPANIES_FILTER_CHANGE,
      SEARCH_CHANGE,
      SORT_PROPERTY_CHANGE,
      MIGRATE_COMPANY_ASYNC.SUCCESS,
      WIZARD_CREATE_TENANT.SUCCESS
    ),
    switchMap(() => {
      const state = state$.value
      return api.msp
        .getPartnerCompanies(
          selectPage(state),
          selectSortProperty(state),
          selectCompaniesFilter(state),
          selectSearch(state)
        )
        .pipe(
          map((response) => fetchCompaniesSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchCompaniesError, error, [400])))
        )
    })
  )

const addCompanyEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(NEW_COMPANY_ADD_ASYNC.INITIATE),
    mergeMap((action) =>
      api.msp.addPartnerCompany(action.payload.data).pipe(
        mergeMap((response) => {
          window.closeModal()
          return of(
            addCompanySuccess(response),
            addToast({ message: 'Company was successfully added!' })
          )
        }),
        catchError((error) => of(epicsErrorHandler(addCompanyError, error, [400])))
      )
    )
  )

const fetchPartnerIncidentsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(PARTNER_INCIDENTS_GET_ASYNC.INITIATE, INCIDENTS_FILTER_CHANGE),
    mergeMap(() =>
      api.msp.getPartnerIncidents(getRangeFromDateFilter(selectTimeFilter(state$.value))).pipe(
        map(({ response }) => fetchPartnerIncidentsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchPartnerIncidentsError, error))),
        takeUntil(action$.pipe(ofType(PARTNER_INCIDENTS_GET_ASYNC.CANCEL)))
      )
    )
  )

const fetchPartnerStatisticsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      PARTNER_STATISTICS_GET_ASYNC.INITIATE,
      NEW_COMPANY_ADD_ASYNC.SUCCESS,
      EDIT_TENANT_APPLY_ASYNC.SUCCESS
    ),
    mergeMap(() =>
      api.msp.getPartnerStatistics().pipe(
        map((response) => fetchPartnerStatisticsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchPartnerStatisticsError, error))),
        takeUntil(action$.pipe(ofType(PARTNER_STATISTICS_GET_ASYNC.CANCEL)))
      )
    )
  )

const fetchPossibleAcquiringPartnersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_POSSIBLE_ACQUIRING_PARTNERS_ASYNC.INITIATE),
    mergeMap(() =>
      api.msp.fetchPossibleAcquiringPartners().pipe(
        mergeMap((response) =>
          of(
            FETCH_POSSIBLE_ACQUIRING_PARTNERS_ASYNC.onSuccess(
              response.possible_acquiring_company_brands
            )
          )
        ),
        catchError((error) =>
          of(epicsErrorHandler(FETCH_POSSIBLE_ACQUIRING_PARTNERS_ASYNC.onError, error))
        ),
        takeUntil(action$.pipe(ofType(FETCH_POSSIBLE_ACQUIRING_PARTNERS_ASYNC.CANCEL)))
      )
    )
  )

const submitPartnerSuccessSubmissionEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(PARTNER_SUCCESS_SUBMISSION_SEND_ASYNC.INITIATE),
    mergeMap((action) =>
      api.msp.submitPartnerSuccessSubmission(action.payload.data).pipe(
        map((response) => submitPartnerSuccessSubmissionSuccess(response)),
        catchError((error) =>
          of(epicsErrorHandler(submitPartnerSuccessSubmissionError, error, [400]))
        ),
        takeUntil(action$.pipe(ofType(PARTNER_SUCCESS_SUBMISSION_SEND_ASYNC.CANCEL)))
      )
    )
  )

const migrateCompanyEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(MIGRATE_COMPANY_ASYNC.INITIATE),
    mergeMap((action) =>
      api.msp
        .migrateCompany(
          action.payload.releasingBrandId,
          action.payload.acquiringBrandId,
          action.payload.companyId
        )
        .pipe(
          mergeMap((response) =>
            of(
              addToast({
                id: 'Migrate_company_Success',
                message: (
                  <span id="Migrate_company_Success">
                    Tenant {action.payload.companyName} was updated successfully
                  </span>
                ),
              }),
              MIGRATE_COMPANY_ASYNC.onSuccess(response)
            )
          ),
          catchError((error) =>
            of(
              addToast({
                type: 'error',
                id: 'Migrate_company_Error',
                message: (
                  <span id="Migrate_company_Error">
                    We were unable to update the tenant plan to {action.payload.companyName}
                    <a href="https://support.ironscales.com/hc/en-us/requests/new">
                      Contact customer Success
                    </a>
                    )
                  </span>
                ),
              }),
              epicsErrorHandler(
                () => MIGRATE_COMPANY_ASYNC.onError(action.payload.companyId),
                error
              )
            )
          ),
          takeUntil(action$.pipe(ofType(MIGRATE_COMPANY_ASYNC.CANCEL)))
        )
    )
  )

// Nesting these seven local epics in their own inner `combineEpics(...)`
// would make them share ONE isolation budget and one crash label — a crash
// in any one of them would count against, and could exhaust, all seven's
// shared retry budget. Flattened so each gets its own. `editTenantEpics` is
// a genuine sub-barrel and stays one leaf.
export default combineEpicsIsolated(
  fetchCompaniesEpic,
  addCompanyEpic,
  fetchPartnerIncidentsEpic,
  fetchPartnerStatisticsEpic,
  submitPartnerSuccessSubmissionEpic,
  migrateCompanyEpic,
  fetchPossibleAcquiringPartnersEpic,
  editTenantEpics
)
